import { Box, Skeleton } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { protoIcon } from './ProtoIcon';
import { KitCard } from './MobileKit';
import { KitChip, KitChips } from './MobileKitExtras';

/**
 * Peças dos dashboards do protótipo mobile (`prototipos-mobile/assets/mobile.css`):
 * `.kpis/.kpi`, `.bars`, `.donut/.legend`, `.hbar`, `.progress` e a linha de chips de
 * período. Valores copiados 1:1 do CSS (claro e escuro via theme.palette). Só devem
 * ser usadas no shell mobile.
 */

const GRAD = (t) => `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.brand2.main})`;
const WARN_LIGHT = '#b45309';

// "R$ 21,4 mil" (>= 1000) ou "R$ 245,80" — recebe número.
export function formatMoneyShort(n) {
    if (!Number.isFinite(n)) return 'R$ 0,00';
    if (Math.abs(n) >= 1000) return `R$ ${(n / 1000).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} mil`;
    return `R$ ${n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Converte o texto monetário da API ("1.234,56") em número.
export function parseMoney(value) {
    if (typeof value === 'number') return value;
    const n = parseFloat(String(value ?? '').replace(/\./g, '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
}

// Períodos dos chips (Hoje / 7 dias / 30 dias / Mês / Ano) -> { start, end } (dayjs).
// 'Mês' é o período padrão das telas reais (não existe no protótipo).
export const PERIOD_OPTIONS = [
    { id: 'today', label: 'Hoje' },
    { id: '7d', label: '7 dias' },
    { id: '30d', label: '30 dias' },
    { id: 'month', label: 'Mês' },
    { id: 'year', label: 'Ano' },
];

export function periodRange(id) {
    const now = dayjs();
    switch (id) {
        case 'today': return { start: now.startOf('day'), end: now.endOf('day') };
        case '7d': return { start: now.subtract(6, 'day').startOf('day'), end: now.endOf('day') };
        case '30d': return { start: now.subtract(29, 'day').startOf('day'), end: now.endOf('day') };
        case 'year': return { start: now.startOf('year'), end: now.endOf('year') };
        default: return { start: now.startOf('month'), end: now.endOf('month') };
    }
}

// Qual chip corresponde às datas atuais (ou null se for um intervalo personalizado).
export function detectPeriod(start, end) {
    if (!start || !end) return null;
    const same = (a, b) => a && b && a.isSame(b, 'day');
    const found = PERIOD_OPTIONS.find((o) => {
        const r = periodRange(o.id);
        return same(r.start, start) && same(r.end, end);
    });
    return found ? found.id : null;
}

// `.chips[data-single]` do período. `options`: [{ id, label }]
export function PeriodChips({ options, activeId, onChange }) {
    return (
        <KitChips>
            {options.map((o) => (
                <KitChip key={o.id} active={o.id === activeId} onClick={() => onChange(o.id)}>{o.label}</KitChip>
            ))}
        </KitChips>
    );
}

// `.kpis` — grade 2 colunas
export function KpiGrid({ children, first }) {
    return <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', mt: first ? 0 : '12px' }}>{children}</Box>;
}

// `.kpi` (+ `.grad`). `delta` = { text, tone: 'up' | 'down' | 'warn' }
export function Kpi({ label, icon, value, delta, grad, loading, compact }) {
    const Icon = icon ? protoIcon(icon) : null;
    return (
        <Box
            sx={(t) => ({
                background: grad ? GRAD(t) : t.palette.background.paper,
                color: grad ? '#fff' : t.palette.text.primary,
                border: grad ? 0 : '1px solid',
                borderColor: 'divider',
                borderRadius: '16px',
                p: '14px',
                boxShadow: grad ? undefined : '0 1px 2px rgba(15,23,42,.06)',
                minWidth: 0,
            })}
        >
            <Box sx={(t) => ({ display: 'flex', alignItems: 'center', justifyContent: 'space-between', color: grad ? 'rgba(255,255,255,.85)' : t.palette.text.disabled, fontSize: 11.5, fontWeight: 600, lineHeight: 1.4 })}>
                <span>{label}</span>
                {Icon && <Icon size={16} />}
            </Box>
            <Box sx={{ fontSize: 21, fontWeight: 800, letterSpacing: '-.02em', m: '6px 0 2px', whiteSpace: 'nowrap', lineHeight: 1.4 }}>
                {loading ? <Skeleton variant="text" width={80} sx={grad ? { bgcolor: 'rgba(255,255,255,.3)' } : undefined} /> : value}
            </Box>
            {!(compact && !delta?.text) && (
            <Box
                sx={(t) => {
                    const tone = delta?.tone || 'up';
                    const color = tone === 'down' ? t.palette.error.main : tone === 'warn' ? WARN_LIGHT : t.palette.success.main;
                    return { fontSize: 11.5, fontWeight: 700, lineHeight: 1.4, minHeight: 16, color: grad ? 'rgba(255,255,255,.85)' : color };
                }}
            >
                {delta?.text}
            </Box>
            )}
        </Box>
    );
}

// `.bars` — colunas verticais. `items`: [{ label, value }]; `alt`: segunda série roxa
// opcional [{ value }] (mesmo índice). Altura proporcional ao maior valor. `pairWidth`: largura fixa (px)
// de cada barra do par (fluxo de caixa usa 12) com gap de 3px.
export function BarsChart({ items, alt, color = 'brand', pairWidth }) {
    const max = Math.max(1, ...items.map((i) => i.value || 0), ...(alt || []).map((i) => i.value || 0));
    const dense = items.length > 12;
    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: dense ? '3px' : '8px', height: 130, pt: '8px' }}>
            {items.map((item, idx) => {
                const barSx = (isAlt, v) => (t) => ({
                    width: pairWidth && alt ? pairWidth : '100%',
                    maxWidth: pairWidth && alt ? pairWidth : 30,
                    borderRadius: '8px 8px 4px 4px',
                    background: isAlt || color === 'alt' ? t.palette.secondary.main : GRAD(t),
                    height: `${Math.max(2, ((v || 0) / max) * 100)}%`,
                    opacity: isAlt || color === 'alt' ? 0.85 : 0.9,
                });
                return (
                    <Box key={`${item.label}-${idx}`} sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '6px', height: '100%', justifyContent: 'flex-end' }}>
                        {alt ? (
                            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: pairWidth ? '3px' : '2px', width: '100%', justifyContent: 'center', height: '100%' }}>
                                <Box sx={barSx(false, item.value)} />
                                <Box sx={barSx(true, alt[idx]?.value)} />
                            </Box>
                        ) : (
                            <Box sx={barSx(false, item.value)} />
                        )}
                        <Box component="span" sx={{ fontSize: 10.5, color: 'text.disabled', fontWeight: 600, lineHeight: 1.4, visibility: dense && idx % 2 === 1 ? 'hidden' : 'visible' }}>{item.label}</Box>
                    </Box>
                );
            })}
        </Box>
    );
}

// `.donut` (conic-gradient) — `segments`: [{ value, color }]; `center`: texto central
export function Donut({ segments, center }) {
    const total = segments.reduce((s, x) => s + (x.value || 0), 0) || 1;
    let acc = 0;
    const stops = segments.map((s) => {
        const from = acc;
        acc += ((s.value || 0) / total) * 100;
        return `${s.color} ${from}% ${acc}%`;
    });
    return (
        <Box
            sx={(t) => ({
                width: 120,
                height: 120,
                borderRadius: '50%',
                background: `conic-gradient(${stops.join(',')})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '&::after': {
                    content: JSON.stringify(String(center ?? '')),
                    width: 78,
                    height: 78,
                    borderRadius: '50%',
                    background: t.palette.background.paper,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 800,
                    fontSize: 15,
                    textAlign: 'center',
                },
            })}
        />
    );
}

// `.legend`
export function Legend({ items }) {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: 12.5, flex: 1, minWidth: 0 }}>
            {items.map((i) => (
                <Box key={i.label} sx={{ display: 'flex', alignItems: 'center', gap: '8px', lineHeight: 1.4 }}>
                    <Box component="i" sx={{ width: 10, height: 10, borderRadius: '3px', display: 'inline-block', flexShrink: 0, background: i.color }} />
                    {i.label}
                </Box>
            ))}
        </Box>
    );
}

// `.card.row-flex` com donut + legenda
export function DonutCard({ segments, center, legend }) {
    return (
        <KitCard sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Donut segments={segments} center={center} />
            <Legend items={legend} />
        </KitCard>
    );
}

// `.hbar` — linhas de barras horizontais. `items`: [{ name, value, text }]
export function HBars({ items, valueWidth = 62 }) {
    const max = Math.max(1, ...items.map((i) => i.value || 0));
    return (
        <>
            {items.map((i) => (
                <Box key={i.name} sx={{ display: 'flex', alignItems: 'center', gap: '10px', m: '10px 0', fontSize: 12.5, lineHeight: 1.4 }}>
                    <Box component="span" sx={{ width: 92, flexShrink: 0, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i.name}</Box>
                    <Box sx={{ flex: 1, height: 10, borderRadius: '5px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
                        <Box sx={(t) => ({ height: '100%', borderRadius: '5px', background: GRAD(t), width: `${((i.value || 0) / max) * 100}%` })} />
                    </Box>
                    <Box component="span" sx={{ width: valueWidth, flexShrink: 0, whiteSpace: 'nowrap', textAlign: 'right', fontWeight: 700 }}>{i.text ?? i.value}</Box>
                </Box>
            ))}
        </>
    );
}

// `.progress` (verde)
export function ProgressBar({ percent, tone = 'green' }) {
    return (
        <Box sx={{ height: 8, borderRadius: '4px', backgroundColor: 'background.subtle', overflow: 'hidden' }}>
            <Box sx={(t) => ({ height: '100%', borderRadius: '4px', width: `${Math.min(100, Math.max(0, percent || 0))}%`, background: tone === 'green' ? t.palette.success.main : GRAD(t) })} />
        </Box>
    );
}

// Estado vazio dentro de um cartão (mesmo texto das telas reais)
export function KitEmptyNote({ children }) {
    return <Box sx={{ textAlign: 'center', color: 'text.disabled', fontSize: 12.5, p: '12px 0', lineHeight: 1.4 }}>{children}</Box>;
}
